import type React from 'react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonClose } from '@/components/buttons';
import { IconComponent } from '@/components/IconComponent';
import { ImageComponent } from '@/components/Image';
import { ScrollBlock } from '@/components/ScrollBlock';
import type { ThemeMode } from '@/helpers/enums';
import { getSelectOptionsHeight } from '@/helpers/primitives';
import { useComponentVisible } from '@/hooks/visibility';
import { InputStructure } from '../../structures/InputStructure';
import {
  type ScOptionsProps,
  ScProfileOptions,
  ScProfileSelectBlock,
  ScSelectSearch,
} from '../styled';
import type { SelectBlockProps, SelectOption } from '../types';

export const ProfileSelectBlock = forwardRef(
  (
    {
      placeholder,
      options,
      selected,
      error,
      hideErrorMessage,
      onSelectOption,
      disabled,
      defaultValue,
      dropdownUp,
      search,
      mode,
      isInputAddon,
      canReset,
      ...scProps
    }: SelectBlockProps,
    forwardedRef?: React.Ref<HTMLDivElement>
  ) => {
    const { ref, openerRef, isComponentVisible, setIsComponentVisible } =
      useComponentVisible(false);

    const handleOpen = useCallback(() => {
      if (!disabled) {
        setIsComponentVisible(!isComponentVisible);
      }
    }, [disabled, isComponentVisible, setIsComponentVisible]);

    const selectedOption = useMemo(
      () => options.find(({ value }) => value === selected),
      [selected, options]
    );

    const { displayedName, displayedPlaceholder } = useMemo(
      () => ({
        displayedName: selectedOption
          ? (selectedOption.nameV ?? selectedOption.name)
          : defaultValue,
        displayedPlaceholder: !(selectedOption || defaultValue) &&
          !!placeholder && <span className="placeholder">{placeholder}</span>,
      }),
      [defaultValue, placeholder, selectedOption]
    );

    const onChange = (value: SelectOption['value']) => {
      onSelectOption && onSelectOption(value);
      handleOpen();
    };

    const handleReset = useCallback(
      (e: any) => {
        e.stopPropagation();
        onSelectOption && onSelectOption('');
      },
      [onSelectOption]
    );

    return (
      <InputStructure error={error} hideErrorMessage={hideErrorMessage}>
        <ScProfileSelectBlock
          active={isComponentVisible}
          className={isComponentVisible ? 'active' : ''}
          disabled={!!disabled}
          error={!!error}
          isInputAddon={isInputAddon}
          mode={mode}
          ref={forwardedRef}
          selected={!!selected}
        >
          <div className="select-button" onClick={handleOpen} ref={openerRef}>
            <div className={`value ${selectedOption?.icon && 'with-icon'}`}>
              {selectedOption?.icon && (
                <ImageComponent
                  alt="option icon"
                  height={24}
                  src={selectedOption.icon}
                  width={24}
                />
              )}
              {displayedName}
              {displayedPlaceholder}
            </div>
            <div className={`${selected && canReset ? 'cross' : 'arrow'}`}>
              {selected && canReset ? (
                <ButtonClose gray isStatic onClick={handleReset} />
              ) : (
                <>
                  <IconComponent name="whiteArrowRight" size="20px" />
                </>
              )}
            </div>
          </div>
          {isComponentVisible && (
            <OptionsBlock
              {...scProps}
              dropdownUp={dropdownUp}
              mode={mode}
              onChange={onChange}
              options={options}
              ref={ref}
              search={search}
              selectedOption={selectedOption}
            />
          )}
        </ScProfileSelectBlock>
      </InputStructure>
    );
  }
);

interface OptionsBlockProps extends ScOptionsProps {
  options: SelectOption[];
  selectedOption?: SelectOption;
  onChange: (value: SelectOption['value']) => void;
  search?: boolean;
  mode?: ThemeMode;
}

export const OptionsBlock = forwardRef<HTMLDivElement, OptionsBlockProps>(
  (
    { options, selectedOption, onChange, search, mode, ...props },
    forwardedRef
  ) => {
    const { t } = useTranslation();

    const [searchValue, setSearchValue] = useState('');

    const ref = useRef<HTMLInputElement>(null);

    useEffect(() => {
      if (!(ref?.current && search)) return;

      ref.current?.focus();
    }, [search]);

    const handleChangeSearchValue = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(e.target.value);
      },
      [setSearchValue]
    );

    const filteredOptions = useMemo(() => {
      if (searchValue === '' || search == null) return options;

      return options.filter(
        ({ nameS }) =>
          !nameS || nameS.toLowerCase().includes(searchValue.toLowerCase())
      );
    }, [searchValue, options, search]);

    return (
      <ScProfileOptions {...props} ref={forwardedRef}>
        {search && (
          <ScSelectSearch>
            <input
              onChange={handleChangeSearchValue}
              placeholder={`${t('search')}...`}
              ref={ref}
              type="text"
            />
          </ScSelectSearch>
        )}
        {filteredOptions.length ? (
          <ScrollBlock
            height={getSelectOptionsHeight(filteredOptions.length, !!search)}
            mode={mode}
            width="100%"
          >
            <ul>
              {filteredOptions.map(({ icon, name, value }, index) => (
                <li
                  className={`${
                    selectedOption?.value === value ? 'active' : ''
                  } ${icon != null ? 'with-icon' : ''}`}
                  key={index}
                  onClick={() => onChange(value)}
                >
                  <div className="content">
                    {icon && (
                      <ImageComponent
                        alt={`${name}`}
                        height={24}
                        src={icon}
                        width={24}
                      />
                    )}
                    {name}
                  </div>
                </li>
              ))}
            </ul>
          </ScrollBlock>
        ) : (
          <ul>
            <li className="not-found">{t('notFound')}</li>
          </ul>
        )}
      </ScProfileOptions>
    );
  }
);
